import React, { useState, useEffect } from 'react';
import { Calendar, Mic, Camera, Radio, Volume2, X } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { SonoReport } from '../../types/sonoReport';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { WorshipReportSelect } from '../ui/WorshipReportSelect';
import { WorshipReport } from '../../types/worshipReport';

interface SonoFormProps {
  onSuccess: () => void;
  onClose: () => void;
}

type StatusOption = 'OK' | 'NOK';
type QualityOption = 'BONNE' | 'MOYENNE' | 'MAUVAISE';

export default function SonoForm({ onSuccess, onClose }: SonoFormProps) {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<Omit<SonoReport, 'id' | 'createdAt'>>({
    date: '',
    worshipReportId: '',
    meetingTypeId: '',
    
    beforeService: {
      materialCheck: 'OK',
      soundQualityTest: 'OK',
      liveStreamingTest: 'OK',
      onlineSoundTest: 'OK',
      onlineVideoTest: 'OK',
      photoEquipmentPrep: 'OK',
    },
    
    duringService: {
      proclamationLaunch: 'OK',
      roomSoundQuality: 'SATISFAISANT',
      liveStreaming: 'OUI',
      onlineSoundQuality: 'BONNE',
      onlineVideoQuality: 'BONNE',
      photoshootDuringService: 'OUI',
    },
    
    afterService: {
      servantsPhotos: 'EFFECTUEE',
      photoMasking: 'EFFECTUEE',
      audioReplayPublication: 'OK',
      videoReplayPublication: 'OK',
      fileArchiving: 'OK',
    },
    needsNotes: ''
  });

  // Auto-populate date and meeting type from selected worship report
  useEffect(() => {
    const fetchWorshipReportData = async () => {
      if (!formData.worshipReportId) {
        setFormData(prev => ({ ...prev, date: '', meetingTypeId: '' }));
        return;
      }

      try {
        const worshipReportRef = doc(db, 'worshipReports', formData.worshipReportId);
        const worshipReportSnap = await getDoc(worshipReportRef);
        
        if (worshipReportSnap.exists()) {
          const worshipReportData = worshipReportSnap.data() as WorshipReport;
          setFormData(prev => ({
            ...prev,
            date: worshipReportData.date,
            meetingTypeId: worshipReportData.meetingTypeId
          }));
        }
      } catch (error) {
        console.error('Error fetching worship report:', error);
      }
    };

    fetchWorshipReportData();
  }, [formData.worshipReportId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.worshipReportId) {
        throw new Error('Veuillez sélectionner le rapport de culte à annexer');
      }
      
      const reportData: Omit<SonoReport, 'id'> = {
        ...formData,
        createdAt: new Date(),
        createdBy: user?.id,
      };

      const docRef = await addDoc(collection(db, 'sonoReports'), {
        ...reportData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Sauvegarder les besoins si présents
      if (formData.needsNotes) {
        await saveNeedsFromNotes({
          reportId: docRef.id,
          reportType: 'sono',
          department: 'Communication & Sono',
          needsNotes: formData.needsNotes,
          reportDate: formData.date
        });
      }

      showNotification('Rapport Communication & Sono créé avec succès', 'Le rapport a été enregistré avec succès.');
      onSuccess();
    } catch (error) {
      console.error('Erreur lors de la création du rapport:', error);
      showNotification('Erreur lors de la création du rapport', 'Une erreur est survenue lors de l\'enregistrement du rapport.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateBeforeService = (field: keyof SonoReport['beforeService'], value: StatusOption) => {
    setFormData(prev => ({
      ...prev,
      beforeService: {
        ...prev.beforeService,
        [field]: value
      }
    }));
  };

  const updateDuringService = (field: keyof SonoReport['duringService'], value: any) => {
    setFormData(prev => ({
      ...prev,
      duringService: {
        ...prev.duringService,
        [field]: value
      }
    }));
  };

  const updateAfterService = (field: keyof SonoReport['afterService'], value: any) => {
    setFormData(prev => ({
      ...prev,
      afterService: {
        ...prev.afterService,
        [field]: value
      }
    }));
  };

  const StatusSelect = ({ value, onChange, label }: { value: StatusOption; onChange: (value: StatusOption) => void; label: string }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="flex space-x-4">
        <label className="inline-flex items-center">
          <input
            type="radio"
            value="OK"
            checked={value === 'OK'}
            onChange={() => onChange('OK')}
            className="form-radio text-[#00665C]"
          />
          <span className="ml-2 text-sm">OK</span>
        </label>
        <label className="inline-flex items-center">
          <input
            type="radio"
            value="NOK"
            checked={value === 'NOK'}
            onChange={() => onChange('NOK')}
            className="form-radio text-[#00665C]"
          />
          <span className="ml-2 text-sm">NOK</span>
        </label>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center">
          <Radio className="mr-2 text-[#00665C]" />
          Nouveau Rapport Communication & Sono
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-8">
        {/* Informations générales */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Informations générales
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <WorshipReportSelect
                value={formData.worshipReportId || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, worshipReportId: value }))}
                required
              departmentCollection="sonoReports"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date du culte</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
               disabled={true}
                className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C] ${
                 'bg-gray-100 cursor-not-allowed'
                }`}
              />
            </div>
            <MeetingTypeSelect
              value={formData.meetingTypeId || ''}
              onChange={(value) => setFormData(prev => ({ ...prev, meetingTypeId: value }))}
              required={false}
             disabled={true}
            />
          </div>
        </div>

        {/* Avant le culte */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-4 flex items-center">
            <Volume2 className="w-4 h-4 mr-2" />
            Avant le culte
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatusSelect
              label="Vérification du matériel Sono"
              value={formData.beforeService.materialCheck}
              onChange={(value) => updateBeforeService('materialCheck', value)}
            />
            <StatusSelect
              label="Test du volume et de la qualité sonore"
              value={formData.beforeService.soundQualityTest}
              onChange={(value) => updateBeforeService('soundQualityTest', value)}
            />
            <StatusSelect
              label="Test du Live Streaming"
              value={formData.beforeService.liveStreamingTest}
              onChange={(value) => updateBeforeService('liveStreamingTest', value)}
            />
            <StatusSelect
              label="Test de la qualité du son en ligne"
              value={formData.beforeService.onlineSoundTest}
              onChange={(value) => updateBeforeService('onlineSoundTest', value)}
            />
            <StatusSelect
              label="Test de la qualité de la vidéo en ligne"
              value={formData.beforeService.onlineVideoTest}
              onChange={(value) => updateBeforeService('onlineVideoTest', value)}
            />
            <StatusSelect
              label="Préparation du matériel photo"
              value={formData.beforeService.photoEquipmentPrep}
              onChange={(value) => updateBeforeService('photoEquipmentPrep', value)}
            />
          </div>
        </div>

        {/* Pendant le culte */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-4 flex items-center">
            <Mic className="w-4 h-4 mr-2" />
            Pendant le culte
          </h3>
          <div className="space-y-4">
            {/* Lancement de la Proclamation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lancement de la Proclamation</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="OK"
                    checked={formData.duringService.proclamationLaunch === 'OK'}
                    onChange={() => updateDuringService('proclamationLaunch', 'OK')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">OK</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="PROBLEME"
                    checked={formData.duringService.proclamationLaunch === 'PROBLEME'}
                    onChange={() => updateDuringService('proclamationLaunch', 'PROBLEME')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Problème</span>
                </label>
              </div>
              {formData.duringService.proclamationLaunch === 'PROBLEME' && (
                <textarea
                  placeholder="Détails du problème..."
                  value={formData.duringService.proclamationLaunchNotes || ''}
                  onChange={(e) => updateDuringService('proclamationLaunchNotes', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                  rows={2}
                />
              )}
            </div>

            {/* Qualité sonore dans la salle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Volume et qualité sonore dans la salle</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="SATISFAISANT"
                    checked={formData.duringService.roomSoundQuality === 'SATISFAISANT'}
                    onChange={() => updateDuringService('roomSoundQuality', 'SATISFAISANT')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Satisfaisant</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="NON_SATISFAISANT"
                    checked={formData.duringService.roomSoundQuality === 'NON_SATISFAISANT'}
                    onChange={() => updateDuringService('roomSoundQuality', 'NON_SATISFAISANT')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Non satisfaisant</span>
                </label>
              </div>
              {formData.duringService.roomSoundQuality === 'NON_SATISFAISANT' && (
                <textarea
                  placeholder="Détails sur la qualité sonore..."
                  value={formData.duringService.roomSoundQualityNotes || ''}
                  onChange={(e) => updateDuringService('roomSoundQualityNotes', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                  rows={2}
                />
              )}
            </div>

            {/* Live Streaming */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Diffusion en direct (Live Streaming)</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="OUI"
                    checked={formData.duringService.liveStreaming === 'OUI'}
                    onChange={() => updateDuringService('liveStreaming', 'OUI')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Oui</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="NON"
                    checked={formData.duringService.liveStreaming === 'NON'}
                    onChange={() => updateDuringService('liveStreaming', 'NON')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Non</span>
                </label>
              </div>
              <textarea
                placeholder="Notes sur le live streaming..."
                value={formData.duringService.liveStreamingNotes || ''}
                onChange={(e) => updateDuringService('liveStreamingNotes', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                rows={2}
              />
            </div>

            {/* Qualités son et vidéo en ligne */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Qualité du son en ligne</label>
                <select
                  value={formData.duringService.onlineSoundQuality}
                  onChange={(e) => updateDuringService('onlineSoundQuality', e.target.value as QualityOption)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                >
                  <option value="BONNE">Bonne</option>
                  <option value="MOYENNE">Moyenne</option>
                  <option value="MAUVAISE">Mauvaise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Qualité de la vidéo en ligne</label>
                <select
                  value={formData.duringService.onlineVideoQuality}
                  onChange={(e) => updateDuringService('onlineVideoQuality', e.target.value as QualityOption)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                >
                  <option value="BONNE">Bonne</option>
                  <option value="MOYENNE">Moyenne</option>
                  <option value="MAUVAISE">Mauvaise</option>
                </select>
              </div>
            </div>

            {/* Prises de vue et problèmes techniques */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prises de vue pendant le culte</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="OUI"
                    checked={formData.duringService.photoshootDuringService === 'OUI'}
                    onChange={() => updateDuringService('photoshootDuringService', 'OUI')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Oui</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="NON"
                    checked={formData.duringService.photoshootDuringService === 'NON'}
                    onChange={() => updateDuringService('photoshootDuringService', 'NON')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Non</span>
                </label>
              </div>
              {formData.duringService.photoshootDuringService === 'OUI' && (
                <textarea
                  placeholder="Détails des prises de vue (louange, message, intercession)..."
                  value={formData.duringService.photoshootDetails || ''}
                  onChange={(e) => updateDuringService('photoshootDetails', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                  rows={2}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Problèmes techniques rencontrés</label>
              <textarea
                placeholder="Décrire les problèmes techniques ou indiquer 'Aucun'..."
                value={formData.duringService.technicalProblems || ''}
                onChange={(e) => updateDuringService('technicalProblems', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Après le culte */}
        <div className="bg-orange-50 p-4 rounded-lg">
          <h3 className="font-semibold text-orange-800 mb-4 flex items-center">
            <Camera className="w-4 h-4 mr-2" />
            Après le culte
          </h3>
          <div className="space-y-4">
            {/* Photos des serviteurs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prise de photos des serviteurs</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="EFFECTUEE"
                    checked={formData.afterService.servantsPhotos === 'EFFECTUEE'}
                    onChange={() => updateAfterService('servantsPhotos', 'EFFECTUEE')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Effectuée</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="NON_EFFECTUEE"
                    checked={formData.afterService.servantsPhotos === 'NON_EFFECTUEE'}
                    onChange={() => updateAfterService('servantsPhotos', 'NON_EFFECTUEE')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Non effectuée</span>
                </label>
              </div>
              <textarea
                placeholder="Notes sur les photos des serviteurs..."
                value={formData.afterService.servantsPhotosNotes || ''}
                onChange={(e) => updateAfterService('servantsPhotosNotes', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                rows={2}
              />
            </div>

            {/* Application des masques */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Application des masques sur les photos</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="EFFECTUEE"
                    checked={formData.afterService.photoMasking === 'EFFECTUEE'}
                    onChange={() => updateAfterService('photoMasking', 'EFFECTUEE')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Effectuée</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="NON_EFFECTUEE"
                    checked={formData.afterService.photoMasking === 'NON_EFFECTUEE'}
                    onChange={() => updateAfterService('photoMasking', 'NON_EFFECTUEE')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">Non effectuée</span>
                </label>
              </div>
              <textarea
                placeholder="Notes sur l'application des masques..."
                value={formData.afterService.photoMaskingNotes || ''}
                onChange={(e) => updateAfterService('photoMaskingNotes', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                rows={2}
              />
            </div>

            {/* Publications replay */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Diffusion des audios du culte sur Replay</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="OK"
                    checked={formData.afterService.audioReplayPublication === 'OK'}
                    onChange={() => updateAfterService('audioReplayPublication', 'OK')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">OK</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="NOK"
                    checked={formData.afterService.audioReplayPublication === 'NOK'}
                    onChange={() => updateAfterService('audioReplayPublication', 'NOK')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">NOK</span>
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="time"
                  placeholder="Heure de publication"
                  value={formData.afterService.audioReplayTime || ''}
                  onChange={(e) => updateAfterService('audioReplayTime', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                />
                <textarea
                  placeholder="Notes sur la publication audio..."
                  value={formData.afterService.audioReplayNotes || ''}
                  onChange={(e) => updateAfterService('audioReplayNotes', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                  rows={1}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Diffusion des vidéos du culte sur Replay</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="OK"
                    checked={formData.afterService.videoReplayPublication === 'OK'}
                    onChange={() => updateAfterService('videoReplayPublication', 'OK')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">OK</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="EN_COURS"
                    checked={formData.afterService.videoReplayPublication === 'EN_COURS'}
                    onChange={() => updateAfterService('videoReplayPublication', 'EN_COURS')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">En cours</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="NOK"
                    checked={formData.afterService.videoReplayPublication === 'NOK'}
                    onChange={() => updateAfterService('videoReplayPublication', 'NOK')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">NOK</span>
                </label>
              </div>
              <textarea
                placeholder="Notes sur la publication vidéo..."
                value={formData.afterService.videoReplayNotes || ''}
                onChange={(e) => updateAfterService('videoReplayNotes', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                rows={2}
              />
            </div>

            {/* Archivage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Archivage et sauvegarde des fichiers</label>
              <div className="flex space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="OK"
                    checked={formData.afterService.fileArchiving === 'OK'}
                    onChange={() => updateAfterService('fileArchiving', 'OK')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">OK</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="NOK"
                    checked={formData.afterService.fileArchiving === 'NOK'}
                    onChange={() => updateAfterService('fileArchiving', 'NOK')}
                    className="form-radio text-[#00665C]"
                  />
                  <span className="ml-2 text-sm">NOK</span>
                </label>
              </div>
              <textarea
                placeholder="Notes sur l'archivage..."
                value={formData.afterService.fileArchivingNotes || ''}
                onChange={(e) => updateAfterService('fileArchivingNotes', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Observations générales */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-4">Observation générale</h3>
          <textarea
            placeholder="Notes générales sur le culte, coordination, améliorations pour le prochain culte..."
            value={formData.generalObservations || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, generalObservations: e.target.value }))}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
            rows={4}
          />
        </div>

        {/* Besoins */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h3 className="font-semibold text-yellow-800 mb-4">Liste des besoins</h3>
          <textarea
            placeholder="Listez les besoins du département (un besoin par ligne)&#10;Exemple:&#10;- Microphones sans fil supplémentaires&#10;- Caméra pour les prises de vue&#10;- Logiciel de montage vidéo"
            value={formData.needsNotes || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, needsNotes: e.target.value }))}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
            rows={4}
          />
          <p className="mt-1 text-xs text-gray-500">
            Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? 'Création...' : 'Créer le Rapport'}
        </button>
      </div>
    </form>
  );
}